import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Spin, Empty } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiPromocionesInstance, authHeaderPromociones } from "../../redux/actions/promociones/promociones";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const isVigente = (p) => {
  const hoy = dayjs().format("YYYY-MM-DD");
  return p.fecha_inicio <= hoy && p.fecha_fin >= hoy;
};

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Eliminacion", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function PromocionDetallePage() {
  const { idPromocion } = useParams();
  const navigate = useNavigate();

  const [promocion, setPromocion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchPromocion = useCallback(() => {
    setLoading(true);
    apiPromocionesInstance.get(`/promociones/${idPromocion}`, { headers: authHeaderPromociones() })
      .then((res) => setPromocion(res.data))
      .catch(() => toast.error("No se pudo cargar la promocion"))
      .finally(() => setLoading(false));
  }, [idPromocion]);

  useEffect(() => { fetchPromocion(); }, [fetchPromocion]);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiPromocionesInstance.get(`/promociones/${idPromocion}/actividad`, { headers: authHeaderPromociones() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idPromocion]);

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar esta promocion?",
      content: "Esta accion desactiva la promocion. No se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await apiPromocionesInstance.delete(`/promociones/${idPromocion}`, { headers: authHeaderPromociones() });
          toast.success("Promocion eliminada");
          navigate("/app/promociones");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!promocion) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Promocion no encontrada" />
      </div>
    );
  }

  const vigente = isVigente(promocion);

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(promocion.nombre || "—").toUpperCase()}</h1>
                <span style={{ padding: "3px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: vigente ? "#dcfce7" : "#fee2e2", color: vigente ? "#15803d" : "#b91c1c" }}>
                  {vigente ? "Vigente" : "Vencida"}
                </span>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> Creada el {fmtFechaCorta(promocion.datetime)}</span>
              </div>
            </div>
            <div className="cd-header-actions">
              <Button icon={<i className="fa-solid fa-pen" />} className="cd-btn-edit" onClick={() => navigate(`/app/promociones/${idPromocion}/editar`)}>
                Editar
              </Button>
              <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-card cd-card-client">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-tag"></i></div>
              <h2 className="cd-card-title">Informacion de la promocion</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
              <div><span className="cd-field-label">Nombre</span><span className="cd-field-value">{promocion.nombre}</span></div>
              <div><span className="cd-field-label">Estado</span><span className="cd-field-value" style={{ color: vigente ? "#15803d" : "#b91c1c" }}>{vigente ? "Vigente" : "Vencida"}</span></div>
              <div><span className="cd-field-label">Fecha de inicio</span><span className="cd-field-value">{promocion.fecha_inicio}</span></div>
              <div><span className="cd-field-label">Fecha de fin</span><span className="cd-field-value">{promocion.fecha_fin}</span></div>
              <div><span className="cd-field-label">Fecha de creacion</span><span className="cd-field-value">{fmtFechaCorta(promocion.datetime)}</span></div>
            </div>
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
